import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Input, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import {
    AssignableDashboardsDocument,
    DeclareDashboardDocument,
    UpdateDashboardDocument,
} from '../../graphql/__generated__/types';
import { catalogueRefusalOf } from './refusals';

/**
 * Declaring a new entry, or correcting the label of one that exists. The two share
 * this component because they compose the same field and read the same refusals;
 * what differs is how much of the entry each may touch, and that difference is forced
 * by the API rather than chosen: see the note on the component below.
 */
export type DashboardDialogMode = 'declare' | 'rename';

interface Props {
    mode: DashboardDialogMode;
    accountId: string;
    accountName: string;
    /** The entry being renamed. Null when declaring. */
    entry?: { id: string; name: string } | null;
    onClose: () => void;
}

/**
 * Declare a dashboard in an account's catalogue, or rename one.
 *
 * **Attention, why renaming touches the name and nothing else.** `updateDashboard` accepts
 * the Metabase identifier and both parameters as a patch, and the API applies them
 * correctly, but **no query returns `DashboardEntry`**, so their current values are
 * unreadable on a loaded screen. `assignableDashboards` carries five fields and none
 * of them is the Metabase id or a parameter slug. A form offering those fields would
 * therefore open **empty**, and sending it would overwrite values the operator was
 * never shown. That is composing blind, which is the one thing this back-office is
 * built not to do, so the fields are not offered and the reason is written on screen
 * rather than left for the next reader to rediscover.
 *
 * This is a hand-over to `client-hub-api`, not a gap to paper over in the front:
 * the catalogue needs an administrator read path: a query returning `DashboardEntry`,
 * archived entries included.
 *
 * **Attention, there is no URL field, and there must never be one.** A Metabase address is a
 * bearer: it forwards, and whoever receives it reads the client's figures with no
 * account and no password. What is declared is an **entry**: an identifier and its
 * perimeter parameter, and the API derives the link from it. The absence of that
 * field is the security property, so the dialog states it rather than leaving an
 * operator hunting for the field they expected.
 *
 * **Attention, the perimeter parameter is required, and the screen says so before the send.**
 * The API refuses without it (`DASHBOARD_PERIMETER_PARAM_REQUIRED`) because the token
 * maker refuses to sign a null parameter: the habilitation would be granted and would
 * never open anything. Letting the refusal fall after the click would teach the
 * operator that the field is optional and the server capricious.
 *
 * **What is *not* pre-empted**: `DASHBOARD_ALREADY_DECLARED`. Whether another live
 * entry occupies the place is a state of the data that changes under the screen, so it
 * is asked for and rendered: greying the button on it would reimplement the server's
 * rule in the front, where it would drift.
 */
const DashboardDialog = ({ mode, accountId, accountName, entry, onClose }: Props) => {
    const declaring = mode === 'declare';

    const [name, setName] = useState(entry?.name ?? '');
    const [metabaseId, setMetabaseId] = useState('');
    const [organizationParam, setOrganizationParam] = useState('');
    const [marketParam, setMarketParam] = useState('');

    /**
     * The catalogue is read by this panel **and** by step 3 of the onboarding journey,
     * under its own variables. Refetching the active query updates the panel; evicting
     * the field is what stops the journey from being served a cached « catalogue vide »
     * for a set of accounts it had already asked about earlier in the session. That
     * stale empty is precisely the loop this lot is about.
     */
    const options = {
        refetchQueries: [AssignableDashboardsDocument],
        awaitRefetchQueries: true,
        update: (cache: { evict: (o: object) => boolean; gc: () => unknown }) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'assignableDashboards' });
            cache.gc();
        },
    };

    const [declare, declareState] = useMutation(DeclareDashboardDocument, options);
    const [rename, renameState] = useMutation(UpdateDashboardDocument, options);

    const { loading, error } = declaring ? declareState : renameState;
    const done = declaring ? declareState.data?.declareDashboard : renameState.data?.updateDashboard;

    const trimmedName = name.trim();
    /**
     * A positive whole number, checked here because the API checks it too
     * (`INVALID_METABASE_DASHBOARD_ID`) and a field the screen lets through only to
     * have it bounce back reads as a broken form. `Number.isInteger` on the parsed
     * value rejects « 12.5 » and « 12abc » alike.
     */
    const parsedMetabaseId = Number(metabaseId.trim());
    const metabaseIdValid = metabaseId.trim().length > 0 && Number.isInteger(parsedMetabaseId) && parsedMetabaseId > 0;
    const perimeterMissing = organizationParam.trim().length === 0;

    const blocked = declaring
        ? trimmedName.length === 0 || !metabaseIdValid || perimeterMissing
        : trimmedName.length === 0 || trimmedName === entry?.name;

    const submit = () => {
        const run = declaring
            ? declare({
                  variables: {
                      organizationId: accountId,
                      name: trimmedName,
                      metabaseDashboardId: parsedMetabaseId,
                      organizationParam: organizationParam.trim(),
                      // Null and not an empty string: the market parameter is genuinely
                      // optional (§3.11 is still open), and «  » is not « absent ».
                      marketParam: marketParam.trim() || null,
                  },
              })
            : rename({ variables: { dashboardId: entry?.id as string, name: trimmedName } });
        // The refusal is read off `error` below; without this every refusal also
        // reaches the console as an unhandled rejection.
        run.catch(() => {});
    };

    const title = declaring
        ? c('Dashboard catalogue').t`Declare a dashboard`
        : c('Dashboard catalogue').t`Rename this dashboard`;

    return (
        <Modal open onClose={onClose} title={title} size={620}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">{accountName}</p>

                    {done ? (
                        <Alert
                            type="success"
                            // Two titles, because « L'entrée est déclarée » on a rename
                            // says the wrong thing about what just happened.
                            title={
                                declaring
                                    ? c('Dashboard catalogue').t`The entry is declared.`
                                    : c('Dashboard catalogue').t`The entry is renamed.`
                            }
                        >
                            <p className="text-sm">
                                {declaring
                                    ? c('Dashboard catalogue')
                                          .t`It is now in this account's catalogue and can be granted, person by person, by an onboarding or from a user's sheet. Declaring the entry is what authorises it; nobody holds it yet.`
                                    : c('Dashboard catalogue')
                                          .t`The label is changed. It is what the client reads, so the people already holding this dashboard will see the new one.`}
                            </p>
                            {/* Said here because this is the ONLY place it can be said: no
                                query returns these values, so after a reload the screen
                                cannot show them again. Worth reading once, carefully. */}
                            {declaring && (
                                <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
                                    <dt className="text-text-600">{c('Dashboard catalogue').t`Metabase id`}</dt>
                                    <dd>{done.metabaseDashboardId}</dd>
                                    <dt className="text-text-600">{c('Dashboard catalogue').t`Account parameter`}</dt>
                                    <dd>{done.organizationParam}</dd>
                                    {done.marketParam && (
                                        <>
                                            <dt className="text-text-600">{c('Dashboard catalogue')
                                                .t`Market parameter`}</dt>
                                            <dd>{done.marketParam}</dd>
                                        </>
                                    )}
                                </dl>
                            )}
                        </Alert>
                    ) : (
                        <>
                            {declaring ? (
                                <Alert type="info">
                                    {c('Dashboard catalogue')
                                        .t`No address is typed here, and that is the point: a Metabase link forwards, and whoever receives it reads this client's figures with no account and no password. You declare an entry — the dashboard's identifier and the parameter that locks it to this account — and the portal builds the link itself, signed for one person at a time.`}
                                </Alert>
                            ) : (
                                <Alert type="info">
                                    {c('Dashboard catalogue')
                                        .t`Only the label can be changed here. The Metabase identifier and the perimeter parameters are not readable on a loaded screen — no query returns them — so a form offering them would open empty and overwrite what you cannot see. Changing those means archiving this entry and declaring it again.`}
                                </Alert>
                            )}

                            <Field
                                id="dashboard-name"
                                label={c('Dashboard catalogue').t`Label read by the client`}
                                hint={c('Dashboard catalogue')
                                    .t`What the client sees in their portal, not the name of the Metabase object. « Consommation 2026 » rather than « dash_conso_v3 ».`}
                            >
                                <Input
                                    id="dashboard-name"
                                    value={name}
                                    onValue={setName}
                                    placeholder={c('Dashboard catalogue').t`Consumption 2026`}
                                />
                            </Field>

                            {declaring && (
                                <>
                                    <Field
                                        id="dashboard-metabase-id"
                                        label={c('Dashboard catalogue').t`Metabase dashboard identifier`}
                                        hint={c('Dashboard catalogue')
                                            .t`The number Metabase gives the dashboard. It is not checked when saved — the portal has no Metabase client — so a wrong number is accepted here and fails loudly when somebody opens it.`}
                                    >
                                        <Input
                                            id="dashboard-metabase-id"
                                            value={metabaseId}
                                            onValue={setMetabaseId}
                                            inputMode="numeric"
                                            placeholder="42"
                                            error={metabaseId.trim().length > 0 && !metabaseIdValid}
                                        />
                                        {metabaseId.trim().length > 0 && !metabaseIdValid && (
                                            <p className="mt-1.5 text-sm text-danger-600">
                                                {c('Dashboard catalogue').t`A positive whole number is expected here.`}
                                            </p>
                                        )}
                                    </Field>

                                    <Field
                                        id="dashboard-organization-param"
                                        label={c('Dashboard catalogue').t`Parameter carrying the account (required)`}
                                        hint={c('Dashboard catalogue')
                                            .t`The slug of the locked Metabase parameter that restricts the dashboard to this account. Without it the access is signed for nobody: the habilitation would be granted and would open nothing, ever — so the declaration is refused rather than saved half-done.`}
                                    >
                                        <Input
                                            id="dashboard-organization-param"
                                            value={organizationParam}
                                            onValue={setOrganizationParam}
                                            placeholder="organization_id"
                                        />
                                    </Field>

                                    <Field
                                        id="dashboard-market-param"
                                        label={c('Dashboard catalogue').t`Parameter carrying the market (optional)`}
                                        hint={c('Dashboard catalogue')
                                            .t`Leave it empty unless the dashboard is also restricted per market. How several markets travel inside a signed token is still open with the BI team, which is why this one is not required.`}
                                    >
                                        <Input
                                            id="dashboard-market-param"
                                            value={marketParam}
                                            onValue={setMarketParam}
                                            placeholder="market_id"
                                        />
                                    </Field>
                                </>
                            )}

                            {/* Beside the button that is refusing, and only once the
                                operator has started composing: shown on an untouched form
                                it reads as an error they have not made yet. */}
                            {declaring && perimeterMissing && trimmedName.length > 0 && (
                                <p className="text-sm text-warning-600">
                                    {c('Dashboard catalogue')
                                        .t`The account parameter is still empty, so this entry cannot be declared: it would be granted and never open anything.`}
                                </p>
                            )}

                            {error && <RefusalNotice refusal={catalogueRefusalOf(error)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button category="default" loading={loading} disabled={blocked} onClick={submit}>
                            {declaring
                                ? c('Dashboard catalogue').t`Declare the dashboard`
                                : c('Dashboard catalogue').t`Rename`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

/**
 * A labelled field with its explanation under the control. The hint is **under** it
 * rather than above: read after the field, it answers « what do I put in there »,
 * which is the question an operator has once they see the box.
 *
 * Local to this folder rather than added to the design system: it is a label, a slot
 * and a paragraph, and the design system fills up fast with shapes that turn out to
 * be one screen's habit: the same reasoning `Card` records. It moves there the day a
 * third screen wants it.
 */
const Field = ({
    id,
    label,
    hint,
    children,
}: {
    id: string;
    label: string;
    hint: string;
    children: React.ReactNode;
}) => (
    <div>
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-text-900">
            {label}
        </label>
        {children}
        <p className="mt-1.5 text-xs text-text-600">{hint}</p>
    </div>
);

export default DashboardDialog;
